import {useEffect, useMemo, useState} from "react"
import {Chip, IconButton, Stack, Typography} from "@mui/joy"
import {Link} from "react-router-dom"
import {CalendarIcon, ChevronDown, ChevronUp, HistoryIcon, SearchIcon, Sparkles, Trash2} from "lucide-react"
import Card from "../components/Card"
import Button from "../components/Button"
import Input from "../components/Input"
import MatchAnalysis from "../components/MatchAnalysis"
import {useAuth} from "../hooks/useAuth"
import {useTranslation} from "react-i18next"
import {
    AnalysisHistoryEntry,
    clearAnalysisHistory,
    readAnalysisHistory,
    removeAnalysis
} from "../services/analysisHistory"
import PageContainer from "../components/layout/PageContainer"

const formatDateTime = (value?: number | string) => {
    const date = new Date(value)

    return !value || isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    })
}

/**
 * Analyses the account has already run.
 *
 * Every completed analysis is stored in the browser, so reopening one costs neither a request nor a
 * prediction from the daily quota.
 */
const History = () => {
    const auth = useAuth()
    const {t} = useTranslation()
    const userId = auth.user?.id || auth.user?._id
    const [entries, setEntries] = useState<AnalysisHistoryEntry[]>([])
    const [query, setQuery] = useState("")
    const [openedId, setOpenedId] = useState<string>()

    // The account arrives after the first render, once the stored session has been resolved
    useEffect(() => setEntries(readAnalysisHistory(userId)), [userId])

    const filtered = useMemo(() => {
        const value = query.trim().toLowerCase()
        if (!value) return entries

        return entries.filter(entry => [entry.homeName, entry.awayName, entry.leagueName]
            .filter(Boolean)
            .some(field => field.toLowerCase().includes(value)))
    }, [entries, query])

    const remove = (id: string) => {
        setEntries(removeAnalysis(userId, id))
        setOpenedId(opened => opened === id ? undefined : opened)
    }

    const clear = () => {
        clearAnalysisHistory(userId)
        setEntries([])
        setOpenedId(undefined)
    }

    return <PageContainer width={{md: openedId ? "48rem" : "38rem", lg: openedId ? "68rem" : "52rem"}}>
        <Stack alignItems={"center"} gap={1}>
            <Typography level={"h2"} textAlign={"center"} fontSize={{xs: "1.6rem", sm: undefined}}>
                {t("historyTitle")}
            </Typography>
            <Typography textAlign={"center"}>
                {t("historySubtitle")}
            </Typography>
            <Typography level={"body-xs"} textAlign={"center"}>
                {t("historyNotice")}
            </Typography>
        </Stack>

        {!entries.length
            ? <Card sx={{alignItems: "center", gap: 2, paddingY: 6}}>
                <HistoryIcon size={40}/>
                <Typography level={"title-lg"}>{t("noAnalysisYet")}</Typography>
                <Typography level={"body-sm"} textAlign={"center"}>
                    {t("noAnalysisYetDescription")}
                </Typography>
                <Link to={"/analyze"} style={{textDecoration: "none"}}>
                    <Button startDecorator={<Sparkles size={18}/>}>{t("analyzeAMatch")}</Button>
                </Link>
            </Card>
            : <Card>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}
                       flexWrap={"wrap"}>
                    <Typography level={"body-sm"}>
                        {t("savedAnalyses", {count: entries.length})}
                    </Typography>
                    <Button size={"sm"} variant={"soft"} color={"danger"} startDecorator={<Trash2 size={16}/>}
                            onClick={clear}
                            sx={{background: "rgba(255,255,255,0.06)", color: "danger.300"}}>
                        {t("clear")}
                    </Button>
                </Stack>

                <Input placeholder={t("filterPlaceholder")}
                       value={query}
                       onChange={event => setQuery(event.target.value)}
                       endDecorator={<SearchIcon/>}/>

                {!filtered.length && <Typography level={"body-sm"}>
                    {t("noAnalysisMatches", {query})}
                </Typography>}

                {filtered.map(entry => {
                    const opened = openedId === entry.id

                    return <Stack key={entry.id} gap={2}
                                  sx={{
                                      padding: {xs: 1.5, sm: 2},
                                      borderRadius: "13px",
                                      border: "1px solid rgba(255,255,255,0.1)",
                                      backgroundColor: "rgba(255,255,255,0.03)"
                                  }}>
                        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
                            <Stack gap={0.5} flex={1} overflow={"hidden"}>
                                <Typography level={"title-md"} noWrap>
                                    {entry.homeName} vs {entry.awayName}
                                </Typography>
                                <Stack direction={"row"} gap={1} flexWrap={"wrap"} alignItems={"center"}>
                                    {entry.leagueName && <Chip size={"sm"} variant={"soft"} color={"primary"}>
                                        {entry.leagueName}
                                    </Chip>}
                                    <Typography level={"body-xs"} startDecorator={<CalendarIcon size={14}/>}>
                                        {formatDateTime(entry.startAt)}
                                    </Typography>
                                </Stack>
                                <Typography level={"body-xs"}>
                                    {t("analyzedOn", {date: formatDateTime(entry.analyzedAt)})}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} gap={0.5} alignItems={"center"}>
                                <IconButton size={"sm"} onClick={() => setOpenedId(opened ? undefined : entry.id)}
                                            aria-label={opened ? t("hideAnalysis") : t("showAnalysis")}>
                                    {opened ? <ChevronUp/> : <ChevronDown/>}
                                </IconButton>
                                <IconButton size={"sm"} color={"danger"} onClick={() => remove(entry.id)}
                                            aria-label={t("removeFromHistory")}>
                                    <Trash2/>
                                </IconButton>
                            </Stack>
                        </Stack>

                        {opened && <MatchAnalysis event={entry.event}/>}
                    </Stack>
                })}
            </Card>}
    </PageContainer>
}

export default History
